import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ClipboardText, FileText, Storefront, Anchor, Package, FileArrowDown, ArrowRight,
  ShieldCheck, ShieldStar,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Pilot Workflow Steps" widget — surfaces a 6-step procurement walkthrough
 * on the PM Dashboard whenever Pilot Mode is active. Designed for live demos:
 * each step shows the right action and a "View as <Role>" button that opens
 * /demo with the corresponding role pre-selected.
 */
const PilotWorkflowSteps = () => {
  const { user, token } = useAuth();
  const propertyId = user?.property_ids?.[0];
  const [state, setState] = useState({ enabled: false, department: null });

  const fetchState = useCallback(async () => {
    if (!propertyId || !token) return;
    try {
      const res = await axios.get(
        `${API_URL}/api/property-settings/${propertyId}/pilot-mode`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setState({
        enabled: Boolean(res.data.pilot_enabled),
        department: res.data.pilot_department || null,
      });
    } catch (_) {/* noop */}
  }, [propertyId, token]);

  useEffect(() => {
    fetchState();
    const handler = (e) => {
      if (e?.detail) {
        setState({ enabled: !!e.detail.enabled, department: e.detail.department || null });
      } else {
        fetchState();
      }
    };
    window.addEventListener('pilot-mode-changed', handler);
    return () => window.removeEventListener('pilot-mode-changed', handler);
  }, [fetchState]);

  if (!state.enabled || !state.department) return null;

  const steps = [
    {
      n: 1,
      title: 'Create Requisition',
      role: 'internal_requester',
      roleLabel: 'Internal Requester',
      desc: 'Department raises a Purchase Requisition (PR). Department is locked to the pilot.',
      Icon: ClipboardText,
      iconBg: 'bg-[#EF4444]/10',
      iconColor: 'text-[#EF4444]',
    },
    {
      n: 2,
      title: 'Convert PR → PO',
      role: null, // current user is PM
      roleLabel: 'You (PM)',
      desc: 'Convert the PR. With pilot thresholds at $1, every PO routes through 4-eyes approval.',
      Icon: FileText,
      iconBg: 'bg-[#3D1D72]/10',
      iconColor: 'text-[#3D1D72]',
      cta: { label: 'Open Requisitions', to: '/requisitions' },
    },
    {
      n: 3,
      title: 'Department Head Approval',
      role: 'department_head',
      roleLabel: 'Department Head',
      desc: 'DH reviews and approves the PO for the pilot department.',
      Icon: ShieldCheck,
      iconBg: 'bg-[#7C3AED]/10',
      iconColor: 'text-[#7C3AED]',
    },
    {
      n: 4,
      title: 'Financial Controller Approval',
      role: 'financial_controller',
      roleLabel: 'Financial Controller',
      desc: 'FC gives final financial sign-off. PO flips to Issued and is sent to vendor.',
      Icon: ShieldStar,
      iconBg: 'bg-[#059669]/10',
      iconColor: 'text-[#059669]',
    },
    {
      n: 5,
      title: 'Vendor Acknowledges',
      role: 'vendor',
      roleLabel: 'Vendor',
      desc: 'Vendor confirms order and uploads commercial invoice / packing list.',
      Icon: Storefront,
      iconBg: 'bg-[#F59E0B]/10',
      iconColor: 'text-[#F59E0B]',
    },
    {
      n: 6,
      title: 'Freight Forwarder Tracks Shipment',
      role: 'freight_forwarder',
      roleLabel: 'Freight Forwarder',
      desc: 'Select Ocean/Air mode, fill sail/AWB, click "Mark as In Transit".',
      Icon: Anchor,
      iconBg: 'bg-[#0F52BA]/10',
      iconColor: 'text-[#0F52BA]',
    },
    {
      n: 7,
      title: 'Receiver Confirms Goods',
      role: 'receiver',
      roleLabel: 'Receiver',
      desc: 'Enter received quantities + upload Vendor Invoice (mandatory).',
      Icon: Package,
      iconBg: 'bg-[#14B8A6]/10',
      iconColor: 'text-[#14B8A6]',
    },
    {
      n: 8,
      title: 'AP Exports Consolidated Package',
      role: 'accounts_payable',
      roleLabel: 'Accounts Payable',
      desc: 'Downloads a single PDF: PO + Receiving Record + Vendor Invoice.',
      Icon: FileArrowDown,
      iconBg: 'bg-[#0891B2]/10',
      iconColor: 'text-[#0891B2]',
    },
  ];

  return (
    <div
      className="bg-white rounded-xl border border-[#3D1D72]/20 shadow-sm overflow-hidden"
      data-testid="pilot-workflow-steps"
    >
      <div className="px-5 py-4 bg-[#F0EBF8] border-b border-[#3D1D72]/15 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#3D1D72]" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Pilot Workflow — Step by Step
          </h2>
          <p className="text-xs text-[#3D1D72]/80 mt-0.5">
            End-to-end demo path for the <span className="font-semibold">{state.department}</span> pilot.
            Click "View as ..." to open <code className="bg-white/60 px-1 rounded">/demo</code> with the role pre-selected.
          </p>
        </div>
        <span className="px-2.5 py-1 rounded-full bg-white text-[#3D1D72] text-xs font-semibold border border-[#3D1D72]/20">
          Thresholds $1 / $1
        </span>
      </div>
      <ol className="divide-y divide-slate-100">
        {steps.map((s) => (
          <li key={s.n} className="px-5 py-3 flex items-start gap-3" data-testid={`pilot-step-${s.n}`}>
            <div className={`w-9 h-9 rounded-lg ${s.iconBg} flex items-center justify-center flex-shrink-0`}>
              <s.Icon size={18} weight="duotone" className={s.iconColor} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-slate-400">STEP {s.n}</span>
                <span className="text-sm font-semibold text-slate-800">{s.title}</span>
                <span className="text-xs text-slate-500 px-1.5 py-0.5 rounded bg-slate-100">{s.roleLabel}</span>
              </div>
              <p className="text-xs text-slate-600 mt-0.5">{s.desc}</p>
            </div>
            <div className="flex-shrink-0">
              {s.role ? (
                <Link
                  to={`/demo?role=${encodeURIComponent(s.role)}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-[#3D1D72] hover:text-[#2D1652] px-2.5 py-1.5 border border-[#3D1D72]/30 rounded-md hover:bg-[#3D1D72]/5"
                  data-testid={`pilot-step-${s.n}-view-as`}
                >
                  View as {s.roleLabel.split('(')[0].trim()}
                  <ArrowRight size={12} />
                </Link>
              ) : s.cta ? (
                <Link
                  to={s.cta.to}
                  className="inline-flex items-center gap-1 text-xs font-medium text-white bg-[#3D1D72] hover:bg-[#2D1652] px-2.5 py-1.5 rounded-md"
                  data-testid={`pilot-step-${s.n}-cta`}
                >
                  {s.cta.label}
                  <ArrowRight size={12} />
                </Link>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default PilotWorkflowSteps;
